import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import CarrierLogo from "@/Shared/Icons/CarrierLogo";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";

interface ClaimSummaryProps {
  shipment: Shipment;
}

/**
 * Which shipment is being claimed against — origin, destination and carrier —
 * centred at the top of the claim form.
 *
 * `align="stretch"` on the outer Row is what gives the vertical rule its
 * height: it stretches to the tallest block rather than needing a fixed one.
 */
export function ClaimsSummary(props: ClaimSummaryProps) {
  const { shipment } = props;
  const selectedQuote = shipment.selectedQuote;

  if (selectedQuote === undefined) {
    return <></>;
  }

  const { pickupLocation, deliveryLocation } = shipment;

  const pickupCity = `${pickupLocation.address.city}, ${pickupLocation.address.stateOrProvinceCode}`;
  const deliveryCity = `${deliveryLocation.address.city}, ${deliveryLocation.address.stateOrProvinceCode}`;

  return (
    <Row gap={6} align="stretch" justify="center" wrap>
      <Row gap={4} align="start">
        <Endpoint_
          businessName={pickupLocation.businessName}
          city={pickupCity}
        />
        {/* Nudged onto the business-name line so the arrow reads as pointing
            between the two names, not between the two blocks. */}
        <ArrowRightIcon
          className="mt-1.5 h-3.5 w-3.5 shrink-0 text-content-muted"
          weight="fill"
        />
        <Endpoint_
          businessName={deliveryLocation.businessName}
          city={deliveryCity}
        />
      </Row>
      <Divider vertical />
      <Stack gap={0} align="center">
        <CarrierLogo
          carrierIdentifier={selectedQuote.carrierIdentifier}
          width={100}
          height={40}
        />
        <T size="xs" color="muted">
          Pro {shipment.proNumber}
        </T>
      </Stack>
    </Row>
  );
}

function Endpoint_(props: { businessName: string; city: string }) {
  return (
    <Stack gap={0} align="start">
      <T size="base" weight="medium">
        {props.businessName}
      </T>
      <T size="sm" color="secondary">
        {props.city}
      </T>
    </Stack>
  );
}
